/**
 * API 客户端封装
 * - JWT Bearer 自动附加
 * - 401 时派发全局事件 → 清理 token 并跳转登录
 * - 错误消息从 ASP.NET Core 常见错误结构提取
 * - 支持多环境配置：VITE_API_BASE_URL
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

export const TOKEN_KEY = 'asg.token'
export const AUTH_EVENT = 'asg:unauthorized'

export class ApiError extends Error {
  status: number
  data: unknown
  constructor(status: number, message: string, data?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** 从后端错误响应中提取可读消息 */
function extractMessage(data: unknown, status: number): string {
  if (!data || typeof data !== 'object') return `请求失败 (${status})`
  const d = data as Record<string, unknown>
  if (typeof d.message === 'string' && d.message) return d.message
  // ModelState 验证错误: { errors: { field: [msg] } }
  if (d.errors && typeof d.errors === 'object') {
    for (const msgs of Object.values(d.errors)) {
      if (Array.isArray(msgs) && msgs.length && typeof msgs[0] === 'string') return msgs[0]
    }
  }
  if (typeof d.title === 'string' && d.title) return d.title
  if (typeof d.error === 'string' && d.error) return d.error
  return `请求失败 (${status})`
}

export interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  /** JSON body */
  body?: unknown
  /** 查询参数（undefined/null 会被忽略） */
  params?: Record<string, string | number | boolean | null | undefined>
  /** FormData body（优先级高于 body） */
  formData?: FormData
  /** 是否携带 token（默认 true） */
  auth?: boolean
  /** 期望响应类型 */
  responseType?: 'json' | 'text' | 'blob'
}

export async function apiFetch<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, params, formData, auth = true, responseType = 'json' } = options

  let url = path.startsWith('http') ? path : `${API_BASE}/api${path}`
  if (params) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.append(k, String(v))
    }
    const q = qs.toString()
    if (q) url += (url.includes('?') ? '&' : '?') + q
  }

  const headers: Record<string, string> = {}
  if (auth) {
    const token = getToken()
    if (token) headers.Authorization = `Bearer ${token}`
  }
  let payload: BodyInit | undefined
  if (formData) {
    payload = formData
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  const res = await fetch(url, { method, headers, body: payload })
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent(AUTH_EVENT))
  }
  if (!res.ok) {
    let data: unknown = null
    try {
      data = await res.clone().json()
    } catch {
      try {
        data = await res.text()
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, extractMessage(data, res.status), data)
  }
  if (res.status === 204) return undefined as T
  if (responseType === 'blob') return (await res.blob()) as T
  if (responseType === 'text') return (await res.text()) as T
  return (await res.json()) as T
}

/** GET 快捷方式 */
export function apiGet<T = unknown>(path: string, params?: ApiOptions['params'], options: ApiOptions = {}): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'GET', params })
}

/** POST 快捷方式 */
export function apiPost<T = unknown>(path: string, body?: unknown, options: ApiOptions = {}): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'POST', body })
}

/** PUT 快捷方式 */
export function apiPut<T = unknown>(path: string, body?: unknown, options: ApiOptions = {}): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'PUT', body })
}

/** DELETE 快捷方式 */
export function apiDelete<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'DELETE' })
}

/** 上传文件（FormData） */
export function apiUpload<T = unknown>(path: string, formData: FormData, options: ApiOptions = {}): Promise<T> {
  return apiFetch<T>(path, { ...options, method: 'POST', formData })
}

/** 触发下载：从后端下载文件并保存 */
export async function apiDownload(path: string, filename?: string, params?: ApiOptions['params']) {
  let url = `${API_BASE}/api${path}`
  if (params) {
    const qs = new URLSearchParams()
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null) qs.append(k, String(v))
    }
    const q = qs.toString()
    if (q) url += (url.includes('?') ? '&' : '?') + q
  }
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  const res = await fetch(url, { headers })
  if (res.status === 401) window.dispatchEvent(new CustomEvent(AUTH_EVENT))
  if (!res.ok) throw new ApiError(res.status, '下载失败', null)
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)
  const name = filename || (m ? decodeURIComponent(m[1]) : url.split('/').pop() || 'download')
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(a.href), 5000)
}
